import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font, SCREEN_WIDTHS } from '../../../constants'
import { getVisibleWidth } from '../../../modules/utils'
import MainModule from '../../../modules/main'

export interface ModalStatusEvent {
  status: 'show' | 'hide'
  modalId: string
  cultureId?: string
  cultureLabel?: string
}

export interface LvcModal {
  modalId: string
  modalStatus: string
  modalTitle: string
  leftTitle: boolean
  largeTitle: boolean
  isFixed: boolean
  mobileTitleCentered: boolean
  fullHeight: boolean
  isFullscreenModalScrollable: boolean
  linearGradient: string
}

export class LvcModal extends MinzeElement {
  attrs: Attrs = [
    'modal-id',
    ['modal-title', null],
    'max-width',
    'hide-when-logged',
    'show-when-logged',
    'narrow',
    'left-title',
    'large-title',
    'is-fixed',
    'mobile-title-centered',
    'full-height',
    'is-fullscreen-modal-scrollable',
    'background',
    ['text-transform', 'uppercase'],
    'title-font-size',
  ]
  reactive: Reactive = [['modalStatus', 'hide']]
  static observedAttributes = ['modal-title']

  onAttributeChange = (name: string, oldValue: string, newValue: string) => {
    if (name === 'modal-title' && oldValue !== newValue) {
      this.rerender()
    }
  }
  _authHandler: any = null
  onStart(): void {
    this._authHandler = this.handleAuthChanged.bind(this)
    MainModule.getInstance().getEE().on('lvc:user_state_changed', this._authHandler)
  }

  onReady(): void {
    this._authHandler = this.handleAuthChanged.bind(this)
    this.updateModalStatus(MainModule.getInstance().getAuth().isLoggedIn())
    if (this.isFixed) document.body.style.overflowY = 'hidden'
  }

  onDestroy(): void {
    MainModule.getInstance().getEE().off('lvc:user_state_changed', this._authHandler)
    if (this.isFixed) document.body.style.overflowY = 'auto'
  }

  async handleAuthChanged(data?: any): Promise<void> {
    this.isLoggedIn = data?.isLoggedIn
    this.updateModalStatus(data?.isLoggedIn)
  }

  updateModalStatus(isLoggedIn?: boolean): void {
    if (
      (this.hideWhenLogged === true && isLoggedIn) ||
      (this.showWhenLogged === true && !isLoggedIn)
    ) {
      this.modalStatus = 'hide'
    } else if (
      (this.hideWhenLogged === true && !isLoggedIn) ||
      (this.showWhenLogged === true && isLoggedIn)
    ) {
      this.modalStatus = 'show'
    } else {
      this.modalStatus = 'show'
    }

    this.rerender()
  }

  html(): string {
    let titleSize = this.largeTitle ? 'titleDesktop' : 'titleDesktopSmall'

    let mobile = getVisibleWidth() < SCREEN_WIDTHS.DESKTOP_SMALL

    if (mobile) {
      titleSize = this.largeTitle ? 'titleMobile' : 'titleMobileSmall'
    }

    return `
    <div id="lvc-modal-overlay" class="lvc-modal-overlay ${this.modalStatus}">
    </div>
    <div id="lvc-modal" class="lvc-modal ${this.modalStatus} ${this.isFixed ? 'fixed' : ''}">
      <!-- Modal content -->
      <div class="lvc-modal-content ${this.narrow ? 'narrow' : ''}">
        <slot name="lvc-modal-top"></slot>

        ${
          this.modalTitle
            ? `<lvc-box mt="10px">
        <span class="lvc-modal-close">&times;</span>

        <lvc-text font-preset=${titleSize} class="lvc-modal-title">${this.modalTitle}</lvc-text>
      </lvc-box>`
            : ''
        }
        <slot name="lvc-modal-content"></slot>
      </div>
    </div>
  `
  }

  css = () => `
    :host {
      ${this.isFullscreenModalScrollable ? '' : `position: fixed;`}
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100%;
      width: 100%;
      z-index: 1400;
      font-family: ${Font.family};
    }
    .lvc-modal-overlay {
      position: absolute; /* Stay in place */
      z-index: 1; /* Sit on top */
      left: 0;
      top: 0;
      width: 100%; /* Full width */
      height: 100%; /* Full height */
      overflow: auto; /* Enable scroll if needed */
      opacity: 0.4
    }

    .lvc-modal-overlay.show *{
      display: block
    }

    .lvc-modal-overlay.hide *{
      display: none
    }

    .lvc-modal {
      display: flex;
      ${this.isFullscreenModalScrollable ? 'position: relative;' : 'position: absolute;'}
      ${this.isFullscreenModalScrollable ? '' : 'padding-top: 145px;'}
      ${this.isFullscreenModalScrollable ? 'min-height: 100%;' : ''}
      left: 0px;
      top: 0px;
      z-index: 1401;
      background: ${this.background ? this.background : 'linear-gradient(to top left, #f3fdaa, rgba(255, 153, 150, 0), #00aebf), linear-gradient(to top right, #fdecd8,rgba(255, 153, 150, 0), #1ade96) rgba(193,254,175,100);'};
      ${this.linearGradient ? `background: ${this.linearGradient};` : ''}
      bottom: 0px;
      ${this.isFullscreenModalScrollable ? '' : 'overflow: auto;'}
      ${this.isFullscreenModalScrollable ? 'padding-top: 110px;' : ''}
      ${this.isFullscreenModalScrollable ? 'padding-bottom: max(5%, 65px);' : ''}
      padding-left: 1rem;
      padding-right: 1rem;
    }

    .lvc-modal.fixed {
      position: fixed;
    }

    .lvc-modal.show {
      display: block
    }

    .lvc-modal.hide {
      display: none
    }

    .lvc-modal-content {
      margin: auto;
      width: 100%;
      border-radius: 32px;
      position: relative;
      ${
        this.fullHeight
          ? `
        height: min-content;
        min-height: -webkit-fill-available;
      `
          : ''
      }
    }

    .lvc-modal-content::before {

    }

    .lvc-modal-close {
      display: none;
      color: #fff;
      font-size: 28px;
      font-weight: ${Font.weight.bold};
      position: absolute;
      top: 45px;
      right: 16px;
    }

    .lvc-modal-close:hover,
    .lvc-modal-close:focus {
      color: #aaaaaa;
      text-decoration: none;
      cursor: pointer;
    }

    .lvc-modal-title {
      text-transform: ${this.textTransform};
      text-align: ${this.leftTitle ? 'left' : 'center'};
      line-height: 1.2;
    }

    @media screen and (max-width: 991px) {
      .lvc-modal-title {
        text-transform: ${this.textTransform};
        text-align: ${this.mobileTitleCentered ? 'center' : 'left'};
      }
    }

    @media only screen and (min-width: 300px) and (max-width: 620px){
      .lvc-modal {
        width: 100%;
        ${this.isFullscreenModalScrollable ? '' : 'height: 100%;'}
        ${this.isFullscreenModalScrollable ? 'min-height: 100%;' : ''}
        ${this.isFullscreenModalScrollable ? 'padding-bottom: 90px;' : ''}
      }
    }

    @media only screen and (min-width: 621px) and (max-width: 960px){
      .lvc-modal {
        width: 100%;
        ${this.isFullscreenModalScrollable ? '' : 'height: 100%;'}
        ${this.isFullscreenModalScrollable ? 'min-height: 100%;' : ''}
        ${this.isFullscreenModalScrollable ? 'padding-bottom: 90px;' : ''}
      }

      .lvc-modal-content {
        border-radius:0px;
        max-width: 540px;
      }

      .narrow {
        max-width:540px;
      }

    }

    @media only screen and (min-width: 960px) and (max-width: 1366px){
      .lvc-modal {
        width: 100%;
      }

      .lvc-modal-content {
        border-radius:0px;
        max-width: 940px;
      }

      .narrow {
        max-width:540px;
      }

    }

    @media only screen and (min-width: 1366px) {
      .lvc-modal {
        width: 100%;
      }

      .lvc-modal-content {
        border-radius:0px;
        max-width: 1366px;
      }

      .narrow {
        max-width:540px;
      }
    }

    @media only screen and (max-width: 991px) {
      :host {
        height: calc(100% - 73px); /* Mobile Navbar adjustment */
      }
      .lvc-modal {
        padding-top: 9rem;
      }
    }
  `

  eventListeners: EventListeners = []
}
